/* global globalThis */
import {supabase} from './supabaseClient'

export const SUPPORT_BODY_LIMIT=5000
export const SUPPORT_REQUEST_TIMEOUT_MS=15000
const uuid=value=>typeof value==='string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
const failure=code=>Object.assign(new Error(code),{code})
const errorCode=error=>{
  const text=String(error?.message||error?.error||'').toLowerCase()
  if(error?.code==='42501'||/forbidden|permission|not_authorized|access_denied/.test(text)) return 'forbidden'
  if(/thread_closed|conversation_closed/.test(text)) return 'closed'
  if(/not_found|missing_thread/.test(text)) return 'not_found'
  if(/client_id|nonce|idempotency|payload_conflict/.test(text)) return 'nonce_conflict'
  if(/rate_limit|too_many/.test(text)) return 'rate_limited'
  return 'unavailable'
}

export function createSupportClientId() {
  if(globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  if(!globalThis.crypto?.getRandomValues) throw failure('unavailable')
  const bytes=globalThis.crypto.getRandomValues(new Uint8Array(16))
  bytes[6]=(bytes[6]&15)|64;bytes[8]=(bytes[8]&63)|128
  return Array.from(bytes,(value,index)=>([4,6,8,10].includes(index)?'-':'')+value.toString(16).padStart(2,'0')).join('')
}

async function supportRpc(name,args,{signal,expectedUserId,client=supabase}={}) {
  const controller=new AbortController()
  let timer,abort
  const deadline=new Promise((_,reject)=>{
    abort=()=>{controller.abort();reject(failure('aborted'))}
    signal?.addEventListener('abort',abort,{once:true})
    timer=setTimeout(()=>{controller.abort();reject(failure('timeout'))},SUPPORT_REQUEST_TIMEOUT_MS)
    if(signal?.aborted) abort()
  })
  const work=Promise.resolve().then(async()=>{
    if(controller.signal.aborted) throw failure('aborted')
    let token
    if(expectedUserId) {
      const {data,error}=await client.auth.getSession()
      const session=data?.session
      if(error||session?.user?.id!==expectedUserId||!session.access_token||controller.signal.aborted) throw failure('auth_changed')
      token=session.access_token
    }
    let query=client.rpc(name,args)
    if(token) query=query.setHeader('Authorization',`Bearer ${token}`)
    const {data,error}=await query.abortSignal(controller.signal)
    if(error) throw failure(errorCode(error))
    if(data?.ok===false) throw failure(errorCode(data))
    if(data===null||data===undefined) throw failure('unavailable')
    return data
  })
  try {return await Promise.race([work,deadline])}
  finally {clearTimeout(timer);signal?.removeEventListener('abort',abort)}
}
const page=(value,mapper)=>{
  if(!value||!Array.isArray(value.items)) throw failure('invalid_response')
  return {items:value.items.map(mapper),hasMore:value.hasMore===true}
}
const text=(value,fallback='')=>typeof value==='string'?value:fallback
const archiveStatus=value=>['queued','sent','failed','partial'].includes(value)?value:null
const lifecycle=value=>({
  ...(value?.closedAt?{closedAt:text(value.closedAt)}:{}),
  ...(archiveStatus(value?.archiveStatus)?{archiveStatus:archiveStatus(value.archiveStatus)}:{}),
})
const safeThread=row=>{
  if(!row||!uuid(row.id)||!uuid(row.memberId)) throw failure('invalid_response')
  return {id:row.id,memberId:row.memberId,companyName:text(row.companyName),subject:text(row.subject,'Hubbing Support'),
    status:row.status==='closed'?'closed':'open',updatedAt:text(row.updatedAt),lastMessagePreview:text(row.lastMessagePreview),
    unreadCount:Number.isSafeInteger(row.unreadCount)?Math.max(0,row.unreadCount):0,...lifecycle(row)}
}
const safeMessage=row=>{
  if(!row||!uuid(row.id)||!uuid(row.threadId)||!uuid(row.senderId)||!['staff','member'].includes(row.senderKind)
    ||typeof row.body!=='string'||!Number.isSafeInteger(Number(row.sequence))||Number(row.sequence)<1) throw failure('invalid_response')
  return {id:row.id,threadId:row.threadId,senderId:row.senderId,senderKind:row.senderKind,body:row.body,createdAt:text(row.createdAt),sequence:Number(row.sequence)}
}
const before=value=>value===undefined||value===null?null:Number.isSafeInteger(Number(value))&&Number(value)>0?Number(value):(()=>{throw failure('invalid_request')})()
const offset=value=>Number.isSafeInteger(value)&&value>=0?value:0
const validateWrite=(body,clientNonce,options)=>{
  if(!options?.expectedUserId) throw failure('auth_changed')
  if(typeof body!=='string'||!body.trim()||body.trim().length>SUPPORT_BODY_LIMIT||!uuid(clientNonce)) throw failure('invalid_request')
}

export async function readSupportAccess(options={}) {
  const value=await supportRpc('hubbing_support_access',{},options)
  if(!value||typeof value.isStaff!=='boolean') throw failure('invalid_response')
  return {isStaff:value.isStaff,canOpen:value.canOpen===true,canReply:value.canReply===true,canSetup:value.canSetup===true}
}
export async function listSupportThreads({offset:pageOffset,status,...options}={}) {
  if(status!==undefined&&status!==null&&!['open','closed','all'].includes(status)) throw failure('invalid_request')
  return page(await supportRpc('hubbing_support_threads',{p_limit:50,p_offset:offset(pageOffset),...(status?{p_status:status}:{})},options),safeThread)
}
export async function listSupportMessages(threadId,{before:sequence,...options}={}) {
  if(!uuid(threadId)) throw failure('invalid_request')
  const result=await supportRpc('hubbing_support_messages',{p_thread_id:threadId,p_before_sequence:before(sequence),p_limit:50},options)
  const value=page(result,safeMessage)
  if(value.items.some(message=>message.threadId!==threadId)) throw failure('invalid_response')
  return {...value,...(['open','closed'].includes(result.threadStatus)?{threadStatus:result.threadStatus}:{}),...lifecycle(result)}
}
export async function listSupportMembers({query='',offset:pageOffset,...options}={}) {
  return page(await supportRpc('hubbing_support_members',{p_query:String(query).trim().slice(0,160),p_limit:30,p_offset:offset(pageOffset)},options),row=>{
    if(!row||!uuid(row.userId)||!uuid(row.companyId)) throw failure('invalid_response')
    return {userId:row.userId,companyId:row.companyId,companyName:text(row.companyName),contactName:text(row.contactName),city:text(row.city),plan:text(row.plan),verificationStatus:text(row.verificationStatus)}
  })
}
export async function openSupportThread({memberId,body,clientNonce},options={}) {
  validateWrite(body,clientNonce,options)
  if(!uuid(memberId)) throw failure('invalid_request')
  const value=await supportRpc('hubbing_support_open',{p_member_id:memberId,p_body:body.trim(),p_client_id:clientNonce},options)
  if(!uuid(value.threadId)) throw failure('invalid_response')
  const message=safeMessage(value.message)
  if(message.threadId!==value.threadId) throw failure('invalid_response')
  return {threadId:value.threadId,message}
}
export async function sendSupportMessage({threadId,body,clientNonce},options={}) {
  validateWrite(body,clientNonce,options)
  if(!uuid(threadId)) throw failure('invalid_request')
  const value=await supportRpc('hubbing_support_send',{p_thread_id:threadId,p_body:body.trim(),p_client_id:clientNonce},options)
  if(value.threadId!==threadId) throw failure('invalid_response')
  const message=safeMessage(value.message)
  if(message.threadId!==threadId) throw failure('invalid_response')
  return {threadId,message}
}
export async function closeSupportThread({threadId,clientNonce},options={}) {
  if(!options.expectedUserId) throw failure('auth_changed')
  if(!uuid(threadId)||!uuid(clientNonce)) throw failure('invalid_request')
  const value=await supportRpc('hubbing_support_close',{p_thread_id:threadId,p_client_id:clientNonce},options)
  if(value.threadId!==threadId||value.status!=='closed'||!value.closedAt||!archiveStatus(value.archiveStatus)) throw failure('invalid_response')
  return {threadId,status:'closed',...lifecycle(value)}
}
export async function markSupportRead({threadId,throughMessageId},options={}) {
  if(!options.expectedUserId||!uuid(threadId)||!uuid(throughMessageId)) throw failure('invalid_request')
  const value=await supportRpc('hubbing_support_mark_read',{p_thread_id:threadId,p_through_message_id:throughMessageId},options)
  if(value.ok!==true) throw failure('invalid_response')
  return {ok:true,unreadCount:Number.isSafeInteger(value.unreadCount)?Math.max(0,value.unreadCount):0}
}
export function subscribeSupportMessages(threadId,onChange,client=supabase) {
  if(threadId&&!uuid(threadId)) return ()=>{}
  if(typeof client.channel!=='function') return ()=>{}
  const channel=client.channel(`support-messages-${threadId||'inbox'}`)
    .on('postgres_changes',{event:'INSERT',schema:'public',table:'support_messages',...(threadId?{filter:`thread_id=eq.${threadId}`}:{})},()=>onChange())
    .on('postgres_changes',{event:'UPDATE',schema:'public',table:'support_threads',...(threadId?{filter:`id=eq.${threadId}`}:{})},()=>onChange())
    .subscribe()
  return ()=>{try{Promise.resolve(client.removeChannel(channel)).catch(()=>{})}catch{}}
}
